import DateInput from '@/components/DateInput'
import { HoverHelp } from '@/tableControls'
import MultiSelectFilter from '@/components/MultiSelectFilter'
import type { MultiSelectOption } from '@/components/MultiSelectFilter'
import type {
  LineAmountFilterOp,
  QboTransactionSearchCriteria,
  RowChangeFilter,
} from '@/services/qboTransactionSearch'

const AMOUNT_FILTER_OPS: { value: LineAmountFilterOp; label: string }[] = [
  { value: 'eq', label: '=' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '≤' },
]

const ROW_CHANGE_FILTER_OPTIONS: { value: RowChangeFilter; label: string }[] = [
  { value: 'all', label: 'All rows' },
  { value: 'with_changes', label: 'With changes' },
  { value: 'clean', label: 'Clean only' },
]

function getFilterInputClass(value: string): string {
  const baseClass = 'input-sm'
  return value ? `${baseClass} ring-2 ring-blue-400 bg-blue-50` : baseClass
}

function splitCommaList(value?: string): string[] {
  if (!value?.trim()) return []
  return value
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
}

function joinCommaList(values: string[]): string {
  return values.map((v) => v.trim()).filter(Boolean).join(', ')
}

function toMultiSelectOptions(
  options: { id: string; label: string; hint?: string; caption?: string }[]
): MultiSelectOption[] {
  return options.map((opt) => ({
    id: opt.id,
    label: opt.label,
    ...(opt.hint ? { hint: opt.hint } : {}),
    ...(opt.caption ? { caption: opt.caption } : {}),
  }))
}

export interface TxnSearchFilterBarProps {
  criteria: QboTransactionSearchCriteria
  onChange: <K extends keyof QboTransactionSearchCriteria>(
    key: K,
    value: QboTransactionSearchCriteria[K]
  ) => void
  idPrefix: string
  vendorOptions: { id: string; label: string; hint?: string }[]
  categoryOptions: { id: string; label: string; hint?: string; caption?: string }[]
  locationOptions: { id: string; label: string; hint?: string }[]
  barClassName?: string
  /** When true, show the pending-changes refine filter (Refine results only). */
  showRowChangeFilter?: boolean
}

export default function TxnSearchFilterBar({
  criteria,
  onChange,
  idPrefix,
  vendorOptions,
  categoryOptions,
  locationOptions,
  barClassName = 'filter-bar--txn-search',
  showRowChangeFilter = false,
}: TxnSearchFilterBarProps) {
  const hasActiveLineAmount = Boolean(criteria.line_amount?.trim())
  const rowChangeFilter = criteria.row_change_filter ?? 'all'
  const hasActiveRowChangeFilter = rowChangeFilter !== 'all'
  const selectedContacts = criteria.contact ?? []
  const vendorMultiOptions = toMultiSelectOptions(vendorOptions)
  const categoryMultiOptions = toMultiSelectOptions(categoryOptions)
  const locationMultiOptions = toMultiSelectOptions(locationOptions)

  return (
    <div className={`filter-bar ${barClassName}`}>
      <div className="flex flex-col gap-1">
        <label
          htmlFor={`${idPrefix}-start-date`}
          className={`text-xs font-medium ${criteria.date_start ? 'text-blue-600' : 'text-gray-600'}`}
        >
          Start date
        </label>
        <DateInput
          className={getFilterInputClass(criteria.date_start ?? '')}
          value={criteria.date_start ?? ''}
          onChange={(v) => onChange('date_start', v)}
          dateFormat="iso"
          placeholder="yyyy-mm-dd"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label
          htmlFor={`${idPrefix}-end-date`}
          className={`text-xs font-medium ${criteria.date_end ? 'text-blue-600' : 'text-gray-600'}`}
        >
          End date
        </label>
        <DateInput
          className={getFilterInputClass(criteria.date_end ?? '')}
          value={criteria.date_end ?? ''}
          onChange={(v) => onChange('date_end', v)}
          dateFormat="iso"
          placeholder="yyyy-mm-dd"
        />
      </div>
      <div className="filter-bar-txn-contact">
        <MultiSelectFilter
          id={`${idPrefix}-vendor`}
          label="Vendor name"
          options={vendorMultiOptions}
          selected={selectedContacts}
          onChange={(value) => onChange('contact', value)}
          placeholder={
            vendorOptions.length === 0
              ? 'Sync QBO data in Settings to load vendors'
              : 'All'
          }
          searchable
        />
      </div>
      <div className="filter-bar-txn-wide flex flex-col gap-1">
        <label
          htmlFor={`${idPrefix}-description`}
          className={`min-w-0 text-xs font-medium ${criteria.description ? 'text-blue-600' : 'text-gray-600'}`}
        >
          <HoverHelp label="Transaction Description" aria-label="About Transaction Description search">
            <>
              <p>
                Supports <strong>AND</strong>, <strong>OR</strong>, and <strong>NOT</strong>{' '}
                operators. Example: <code>office AND supplies NOT paper</code>
              </p>
              <p>
                Sync QBO lists from <strong>Settings</strong> before using the vendor,
                category, and location dropdowns.
              </p>
            </>
          </HoverHelp>
        </label>
        <input
          id={`${idPrefix}-description`}
          className={getFilterInputClass(criteria.description ?? '')}
          value={criteria.description ?? ''}
          onChange={(e) => onChange('description', e.target.value)}
          placeholder='e.g. "office supplies" AND paper NOT envelope'
        />
      </div>
      <div className="flex flex-col gap-1">
        <label
          htmlFor={`${idPrefix}-ref`}
          className={`text-xs font-medium ${criteria.doc_number ? 'text-blue-600' : 'text-gray-600'}`}
        >
          Transaction reference number
        </label>
        <input
          id={`${idPrefix}-ref`}
          className={getFilterInputClass(criteria.doc_number ?? '')}
          value={criteria.doc_number ?? ''}
          onChange={(e) => onChange('doc_number', e.target.value)}
          placeholder="Search…"
        />
      </div>
      <div className="flex flex-col gap-1">
        <label
          htmlFor={`${idPrefix}-memo`}
          className={`text-xs font-medium ${criteria.memo ? 'text-blue-600' : 'text-gray-600'}`}
        >
          Memo
        </label>
        <input
          id={`${idPrefix}-memo`}
          className={getFilterInputClass(criteria.memo ?? '')}
          value={criteria.memo ?? ''}
          onChange={(e) => onChange('memo', e.target.value)}
          placeholder="Search…"
        />
      </div>
      <div className="filter-bar-txn-wide filter-bar-break flex flex-col gap-1">
        <label
          htmlFor={`${idPrefix}-line-desc`}
          className={`min-w-0 text-xs font-medium ${criteria.line_description ? 'text-blue-600' : 'text-gray-600'}`}
        >
          <HoverHelp label="Line item description" aria-label="About Line item description search">
            <p>
              Unquoted text matches as a substring; multi-word queries also match when every
              word appears anywhere in the description (e.g. <code>dry erase</code>). A quoted
              phrase requires that exact contiguous phrase (e.g.{' '}
              <code>&quot;dry erase&quot;</code>). Also supports <strong>AND</strong>,{' '}
              <strong>OR</strong>, and <strong>NOT</strong> operators. Example:{' '}
              <code>supplies AND paper NOT envelope</code>
            </p>
          </HoverHelp>
        </label>
        <input
          id={`${idPrefix}-line-desc`}
          className={getFilterInputClass(criteria.line_description ?? '')}
          value={criteria.line_description ?? ''}
          onChange={(e) => onChange('line_description', e.target.value)}
          placeholder='e.g. "office supplies" AND paper NOT envelope'
        />
      </div>
      <MultiSelectFilter
        id={`${idPrefix}-line-location`}
        label="Line item location"
        options={locationMultiOptions}
        selected={splitCommaList(criteria.line_location)}
        onChange={(value) => onChange('line_location', joinCommaList(value))}
        placeholder={
          locationOptions.length === 0 ? 'Sync QBO data in Settings' : 'All'
        }
        searchable
      />
      <MultiSelectFilter
        id={`${idPrefix}-line-category`}
        label="Line item category"
        options={categoryMultiOptions}
        selected={splitCommaList(criteria.line_category)}
        onChange={(value) => onChange('line_category', joinCommaList(value))}
        placeholder={
          categoryOptions.length === 0 ? 'Sync QBO data in Settings' : 'All'
        }
        searchable
      />
      <div className="filter-bar-amount flex flex-col gap-1">
        <label
          htmlFor={`${idPrefix}-line-amount`}
          className={`text-xs font-medium ${hasActiveLineAmount ? 'text-blue-600' : 'text-gray-600'}`}
        >
          Line item amount
        </label>
        <div className="filter-bar-amount-controls">
          <select
            id={`${idPrefix}-line-amount-op`}
            aria-label="Line amount comparison"
            className={
              hasActiveLineAmount
                ? 'filter-bar-amount-op select ring-2 ring-blue-400 bg-blue-50'
                : 'filter-bar-amount-op select'
            }
            value={criteria.line_amount_op ?? 'eq'}
            onChange={(e) => onChange('line_amount_op', e.target.value as LineAmountFilterOp)}
          >
            {AMOUNT_FILTER_OPS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <input
            id={`${idPrefix}-line-amount`}
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            className={`filter-bar-amount-value ${getFilterInputClass(criteria.line_amount ?? '')}`}
            value={criteria.line_amount ?? ''}
            onChange={(e) => onChange('line_amount', e.target.value)}
          />
        </div>
      </div>
      {showRowChangeFilter ? (
        <div className="flex flex-col gap-1">
          <label
            htmlFor={`${idPrefix}-row-change`}
            className={`text-xs font-medium ${
              hasActiveRowChangeFilter ? 'text-blue-600' : 'text-gray-600'
            }`}
          >
            Row changes
          </label>
          <select
            id={`${idPrefix}-row-change`}
            className={
              hasActiveRowChangeFilter
                ? 'select input-sm ring-2 ring-blue-400 bg-blue-50'
                : 'select input-sm'
            }
            value={rowChangeFilter}
            onChange={(e) => onChange('row_change_filter', e.target.value as RowChangeFilter)}
          >
            {ROW_CHANGE_FILTER_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      ) : null}
    </div>
  )
}
